import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Stack, Alert } from '@mui/material';
import { Add, Groups } from '@mui/icons-material';
import { PageContainer, PageCard } from '../../common/components';
import { TeamsGrid } from '../components/TeamList';
import { TeamAddEditDialog } from '../components/dialogs/TeamAddEdit';
import { useGetTeamsQuery, TeamView, TeamListView } from '../../../services/TeamService';
import { BackToDashboardButton } from '../../common/components/navigation';

/**
 * TeamEdit - Main page component for managing teams
 * Provides a complete interface for team administration with grid view and CRUD operations
 */
const TeamEdit: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  
  // Fetch teams list data
  const { 
    data: teams = [], 
    error, 
    isLoading,
    refetch 
  } = useGetTeamsQuery({ status: undefined });



  const handleAddTeam = () => {
    setSelectedTeamId(null);
    setDialogOpen(true);
  };

  const handleEditTeam = (team: TeamListView) => {
    // Pass the team ID to the dialog which will fetch the full team data
    setSelectedTeamId(team.id);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedTeamId(null);
  };

  const handleTeamSaved = (savedTeam: TeamView) => {
    // Refresh the teams list after save
    refetch();
  };

  if (error) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center">
          <PageCard>
            <Alert severity="error">
              Failed to load teams. Please try again later.
            </Alert>
          </PageCard>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard>
          {/* Header */}
          <Box sx={{ 
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #3b82f6',
            p: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Groups sx={{ color: '#3b82f6', fontSize: 28 }} />
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#1e293b',
                    lineHeight: 1.2
                  }}
                >
                  Team Management
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ color: '#64748b', mt: 0.5 }}
                >
                  Manage teams and team members for your organization
                </Typography>
              </Box>
            </Box>
            
            <Stack direction="row" spacing={2}>
              <BackToDashboardButton />
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddTeam}
                sx={{
                  backgroundColor: '#162346',
                  color: 'white',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: '#1a2a52'
                  }
                }}
              >
                Add Team
              </Button>
            </Stack>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3, backgroundColor: '#ffffff' }}>
            <Stack spacing={3}>
              {/* Search */}
              <Box sx={{ maxWidth: 400 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search teams by name, description, status, or members..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f8fafc',
                      '&:hover': {
                        backgroundColor: '#f1f5f9'
                      }
                    }
                  }}
                />
              </Box>

              {/* Teams Grid */}
              <Box>
                <TeamsGrid
                  teams={teams}
                  searchText={searchText}
                  onRowSelect={handleEditTeam}
                  isLoading={isLoading}
                />
              </Box>
            </Stack>
          </Box>

          {/* Add/Edit Dialog */}
          <TeamAddEditDialog
            open={dialogOpen}
            onClose={handleDialogClose}
            teamId={selectedTeamId}
            onSave={handleTeamSaved}
          />
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default TeamEdit;